/**
 * useInvoice - Hook para obtener una factura individual por ID
 *
 * Utiliza React Query para gestionar el estado de carga, error y cache
 * de una factura específica obtenida por su ID desde la API.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { InvoicesViewApiAdapter } from '../adapters/InvoicesViewApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import type { Invoice } from '../models/InvoicesView';

export const useInvoice = () => {
  const { preInvoiceId } = useParams<{ preInvoiceId: string }>();
  const invoiceIdNumber = parseInt(preInvoiceId || '0', 10);

  const invoicesAdapter = new InvoicesViewApiAdapter(createHttpClient(apiClientWH));

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery<Invoice>({
    queryKey: ['invoice', invoiceIdNumber],
    queryFn: () => invoicesAdapter.getInvoiceById(invoiceIdNumber),
    enabled: invoiceIdNumber > 0,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
  });

  const invoice = useMemo(() => {
    if (!data) {
      return undefined;
    }

    const fallbackCode = data.code ?? data.erp_number ?? (data.id ? `#${data.id}` : '-');

    return {
      ...data,
      code: fallbackCode,
    };
  }, [data]);

  return {
    invoice,
    isLoading,
    error: error ? (error as Error).message : null,
    refetch,
  };
};
